import React from 'react';
import {
  Box,
  Breadcrumbs as MUIBreadcrumbs,
  Link,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useAppTheme } from '../../app/providers/ThemeProvider';

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactElement;
  truncate?: boolean;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  maxItems?: number;
  itemsBeforeCollapse?: number;
  itemsAfterCollapse?: number;
  showHomeIcon?: boolean;
  className?: string;
}

const Breadcrumbs = React.memo<BreadcrumbsProps>(
  ({
    items,
    maxItems = 8,
    itemsBeforeCollapse = 1,
    itemsAfterCollapse = 2,
    showHomeIcon = true,
    className,
  }) => {
    const location = useLocation();
    const muiTheme = useTheme();
    const { customTokens: tokens } = useAppTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

    // Adjust for mobile - show fewer items
    const effectiveMaxItems = isMobile ? Math.min(maxItems, 3) : maxItems;
    const effectiveItemsAfterCollapse = isMobile ? 1 : itemsAfterCollapse;

    // Add home breadcrumb if not already present and showHomeIcon is true
    const breadcrumbItems: BreadcrumbItem[] = React.useMemo(() => {
      if (items.length === 0) return [];

      const hasHome = items.some(item => item.path === '/');
      if (!hasHome && showHomeIcon && location.pathname !== '/') {
        return [
          {
            label: 'Home',
            path: '/',
            icon: <HomeIcon fontSize="small" />,
          },
          ...items,
        ];
      }
      return items;
    }, [items, showHomeIcon, location.pathname]);

    if (breadcrumbItems.length === 0) {
      return null;
    }

    // Function to truncate text on mobile
    const truncateText = (text: string, maxLength: number = 20): string => {
      if (!isMobile || text.length <= maxLength) {
        return text;
      }
      return `${text.substring(0, maxLength - 3)}...`;
    };

    return (
      <Box
        className={className}
        sx={{
          py: tokens.spacing[2],
          px: { xs: tokens.spacing[2], sm: 0 },
        }}
      >
        <MUIBreadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          maxItems={effectiveMaxItems}
          itemsBeforeCollapse={itemsBeforeCollapse}
          itemsAfterCollapse={effectiveItemsAfterCollapse}
          aria-label="Navigation breadcrumb"
          sx={{
            '& .MuiBreadcrumbs-separator': {
              color: 'text.secondary',
              mx: tokens.spacing[1],
            },
            '& .MuiBreadcrumbs-li': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        >
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;
            const displayLabel = item.truncate ? truncateText(item.label) : item.label;

            if (isLast || !item.path) {
              return (
                <Typography
                  key={`${item.path || 'current'}-${index}`}
                  color="text.primary"
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontWeight: tokens.typography.fontWeight.medium,
                    fontSize: {
                      xs: tokens.typography.fontSize.sm,
                      sm: tokens.typography.fontSize.base,
                    },
                  }}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.icon && (
                    <Box
                      component="span"
                      sx={{
                        mr: tokens.spacing[1],
                        display: 'flex',
                        alignItems: 'center',
                        '& svg': {
                          fontSize: '1.1rem',
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                  )}
                  {displayLabel}
                </Typography>
              );
            }

            return (
              <Link
                key={`${item.path}-${index}`}
                component={RouterLink}
                to={item.path}
                underline="hover"
                color="inherit"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: {
                    xs: tokens.typography.fontSize.sm,
                    sm: tokens.typography.fontSize.base,
                  },
                  fontWeight: tokens.typography.fontWeight.medium,
                  transition: `color ${tokens.motion.duration.fast} ${tokens.motion.easing.ease}`,
                  '&:hover': {
                    color: 'primary.main',
                  },
                  '&:focus': {
                    outline: `2px solid ${muiTheme.palette.primary.main}`,
                    outlineOffset: '2px',
                    borderRadius: tokens.radii.sm,
                  },
                }}
                aria-label={`Navigate to ${item.label}`}
              >
                {item.icon && (
                  <Box
                    component="span"
                    sx={{
                      mr: tokens.spacing[1],
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': {
                        fontSize: '1.1rem',
                      },
                    }}
                  >
                    {item.icon}
                  </Box>
                )}
                {displayLabel}
              </Link>
            );
          })}
        </MUIBreadcrumbs>
      </Box>
    );
  },
);

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
